// Single source of truth for /tools/free-homebuying-readiness-tools.

const SITE = "https://www.homeapproach.com";

export const PAGE_PATH = "/tools/free-homebuying-readiness-tools";
export const PAGE_URL = `${SITE}${PAGE_PATH}`;
export const PAGE_TITLE = "Free Tools to Check Your Homebuying Readiness";
export const PAGE_DESCRIPTION =
  "The free tools that tell you whether you are ready to buy a home — readiness quizzes, homebuyer courses, affordability calculators, credit tools with no hard pull, and free HUD-approved counseling — compared on cost, credit impact, certificate, and what data you have to hand over.";

export const HERO_PROMISE =
  "You can answer \"am I ready to buy?\" without paying anyone and without a hard credit pull. Start with our three free tools below, then use the comparison table to pick the outside tool that fills whatever gap is left.";

export const SECTIONS = [
  { id: "our-tools", title: "Our free tools first" },
  { id: "start-here", title: "Start here: pick your question" },
  { id: "quizzes", title: "Readiness quizzes" },
  { id: "courses", title: "Homebuyer courses" },
  { id: "calculators", title: "Affordability calculators" },
  { id: "credit-tools", title: "Credit tools" },
  { id: "counseling", title: "Free counseling" },
  { id: "comparison", title: "Compare every tool" },
  { id: "why-listed", title: "Why these tools made the list" },
  { id: "faq", title: "Common questions" },
];

export type ReadinessStage = "exploring" | "preparing" | "ready";

export interface OurTool {
  id: string;
  label: string;
  prompt: string;
  blurb: string;
  href: string;
  cta: string;
  stage: ReadinessStage;
}

export const OUR_TOOLS: OurTool[] = [
  {
    id: "affordability",
    label: "Check affordability",
    prompt: "What can I actually afford — not what a lender will approve?",
    blurb:
      "Runs your real budget, including the expenses debt-to-income ratios ignore, and returns a comfort payment alongside the bank's maximum so you can see the gap.",
    href: "/tools/real-affordability",
    cta: "Run the affordability check",
    stage: "preparing",
  },
  {
    id: "credit",
    label: "Check credit readiness",
    prompt: "Is my credit where a mortgage lender needs it to be?",
    blurb:
      "A soft-pull score check with mortgage-specific context: the middle-score rule, the thresholds that change your rate, and what to fix first. No hard inquiry.",
    href: "/check-my-score",
    cta: "Check my score free",
    stage: "preparing",
  },
  {
    id: "plan",
    label: "Get a plan",
    prompt: "What money is out there for me, and what do I do next?",
    blurb:
      "Stacks the federal, state, county, and local down payment help you may qualify for into one ordered plan with the next action for each layer.",
    href: "/stack",
    cta: "Build my plan",
    stage: "ready",
  },
];

export interface StartHereIntent {
  id: string;
  question: string;
  answer: string;
  href: string;
  cta: string;
  category: string;
}

export const START_HERE: StartHereIntent[] = [
  {
    id: "quiz",
    question: "I want a free readiness quiz",
    answer:
      "A five-minute scored quiz that grades credit, savings, debt, income stability, and timeline, then tells you which one is holding you back.",
    href: "/tools/homebuyer-readiness-score",
    cta: "Take the readiness quiz",
    category: "quizzes",
  },
  {
    id: "calculator",
    question: "I want a free affordability calculator",
    answer:
      "A calculator that separates the lender's maximum from a payment you can actually live with, and shows the monthly costs buyers forget.",
    href: "/tools/real-affordability",
    cta: "Open the calculator",
    category: "calculators",
  },
  {
    id: "score",
    question: "I want my free credit score",
    answer:
      "A soft-pull score plus the mortgage context most free score apps skip: which of your three scores lenders use, and what moves it fastest.",
    href: "/check-my-score",
    cta: "Get my free score",
    category: "credit-tools",
  },
  {
    id: "no-hard-pull",
    question: "I want pre-approval options with no hard pull",
    answer:
      "Pre-qualification and soft-pull estimates let you test loan scenarios without an inquiry on your report. A hard pull only comes when you formally apply — and mortgage inquiries inside a 14-45 day window count as one.",
    href: "/tools/compare-lender-quotes",
    cta: "Compare quotes without a hard pull",
    category: "credit-tools",
  },
];

export interface ToolEntry {
  id: string;
  name: string;
  source: "Government" | "GSE" | "Nonprofit" | "Marketplace" | "Home Approach";
  what: string;
  cost: string;
  noCreditPull: string;
  certificate: string;
  personalization: string;
  dataNeeded: string;
  nextStep: string;
  href: string;
  internal?: boolean;
}

export interface ToolCategory {
  id: string;
  title: string;
  intro: string;
  stage: ReadinessStage;
  ctaLabel: string;
  ctaHref: string;
  tools: ToolEntry[];
}

export const CATEGORIES: ToolCategory[] = [
  {
    id: "quizzes",
    title: "Readiness quizzes",
    intro:
      "A quiz is the fastest way to find your weakest link. It will not tell you a price range, but it will tell you whether credit, savings, or debt is the thing to fix first.",
    stage: "exploring",
    ctaLabel: "Score your readiness now",
    ctaHref: "/tools/homebuyer-readiness-score",
    tools: [
      {
        id: "ha-readiness",
        name: "Home Approach Readiness Score",
        source: "Home Approach",
        what: "Weighted 0-100 score across credit, savings, debt, income stability, and timeline, with a tip for each band.",
        cost: "Free",
        noCreditPull: "Yes — no pull at all",
        certificate: "No",
        personalization: "High — your inputs, scored and explained",
        dataNeeded: "Rough numbers only, no account linking",
        nextStep: "Fix your lowest-scoring factor",
        href: "/tools/homebuyer-readiness-score",
        internal: true,
      },
      {
        id: "fanniemae-quiz",
        name: "Fannie Mae HomeView readiness check",
        source: "GSE",
        what: "Short self-assessment built into a national homebuyer education platform.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "Yes, when you finish the full course",
        personalization: "Low — general guidance",
        dataNeeded: "Account signup",
        nextStep: "Continue into the free course",
        href: "https://homeview.fanniemae.com/",
      },
      {
        id: "cfpb-buying",
        name: "CFPB Buying a House journey",
        source: "Government",
        what: "Step-by-step readiness walkthrough from the federal consumer protection agency.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "Low — educational path",
        dataNeeded: "None",
        nextStep: "Work through the affordability step",
        href: "https://www.consumerfinance.gov/owning-a-home/",
      },
    ],
  },
  {
    id: "courses",
    title: "Homebuyer courses",
    intro:
      "Most down payment assistance programs require a homebuyer education certificate. Take the course early — the certificate is often valid for a year, and having it in hand stops it from delaying your closing.",
    stage: "preparing",
    ctaLabel: "See which programs require a certificate",
    ctaHref: "/tools/combining-assistance-programs",
    tools: [
      {
        id: "homeview",
        name: "Fannie Mae HomeView",
        source: "GSE",
        what: "Free national homebuyer education course, seven modules, accepted by many lenders and programs.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "Yes",
        personalization: "Low — standard curriculum",
        dataNeeded: "Account signup",
        nextStep: "Save the certificate PDF",
        href: "https://homeview.fanniemae.com/",
      },
      {
        id: "framework",
        name: "Framework Homeownership",
        source: "Nonprofit",
        what: "Widely accepted online course; fee varies and is sometimes covered by a lender or program.",
        cost: "Often paid, sometimes sponsored",
        noCreditPull: "Yes",
        certificate: "Yes",
        personalization: "Low",
        dataNeeded: "Account signup",
        nextStep: "Ask if your program sponsors the fee",
        href: "https://frameworkhomeownership.org/",
      },
      {
        id: "hud-course",
        name: "HUD-approved agency workshops",
        source: "Government",
        what: "In-person or virtual classes from local HUD-approved agencies, plus a private counseling option.",
        cost: "Free or low, disclosed upfront",
        noCreditPull: "Yes",
        certificate: "Yes",
        personalization: "Medium — local programs covered",
        dataNeeded: "Registration details",
        nextStep: "Find your local agency",
        href: "/tools/find-housing-counselor",
        internal: true,
      },
    ],
  },
  {
    id: "calculators",
    title: "Affordability calculators",
    intro:
      "Almost every calculator online answers the lender's question — the biggest loan you can be approved for. Very few answer yours: the payment that still leaves your life intact.",
    stage: "preparing",
    ctaLabel: "Find your real affordability number",
    ctaHref: "/tools/real-affordability",
    tools: [
      {
        id: "ha-real-affordability",
        name: "Home Approach Real Affordability",
        source: "Home Approach",
        what: "Shows bank maximum next to a comfort target, and adds the ghost expenses standard calculators skip.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "High",
        dataNeeded: "Income, debts, savings, rough expenses",
        nextStep: "Set your target price and payment",
        href: "/tools/real-affordability",
        internal: true,
      },
      {
        id: "ha-cash-to-close",
        name: "Home Approach Cash to Close",
        source: "Home Approach",
        what: "Breaks down the total cash you need at the table and why it moves between estimate and closing.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "Medium",
        dataNeeded: "Price, down payment, credits",
        nextStep: "Budget your closing cash",
        href: "/tools/cash-to-close",
        internal: true,
      },
      {
        id: "cfpb-explorer",
        name: "CFPB Explore Interest Rates",
        source: "Government",
        what: "Neutral, ad-free rate ranges by credit score, state, and loan type.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "Medium",
        dataNeeded: "Score range, state, price",
        nextStep: "Sanity-check any quote you receive",
        href: "https://www.consumerfinance.gov/owning-a-home/explore-rates/",
      },
    ],
  },
  {
    id: "credit-tools",
    title: "Credit tools",
    intro:
      "Checking your own credit is a soft inquiry and never lowers your score. Only a lender's formal application creates a hard pull — and mortgage shopping inside a short window counts as a single inquiry.",
    stage: "preparing",
    ctaLabel: "Check your score with no hard pull",
    ctaHref: "/check-my-score",
    tools: [
      {
        id: "ha-score",
        name: "Home Approach Check My Score",
        source: "Home Approach",
        what: "Soft-pull score with mortgage-specific context on the middle-score rule and rate thresholds.",
        cost: "Free",
        noCreditPull: "Yes — soft pull only",
        certificate: "No",
        personalization: "High",
        dataNeeded: "Identity verification",
        nextStep: "Work the fix-first list",
        href: "/check-my-score",
        internal: true,
      },
      {
        id: "annualcreditreport",
        name: "AnnualCreditReport.com",
        source: "Government",
        what: "The federally authorized source for your full reports from all three bureaus.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "High — your actual file",
        dataNeeded: "Identity verification",
        nextStep: "Dispute anything inaccurate",
        href: "https://www.annualcreditreport.com/",
      },
      {
        id: "ha-raise-score",
        name: "Home Approach Raise Your Score",
        source: "Home Approach",
        what: "The fastest legitimate score moves before a mortgage, in the order they pay off.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "Medium",
        dataNeeded: "None",
        nextStep: "Start the 30-60 day plan",
        href: "/tools/raise-credit-score-before-mortgage",
        internal: true,
      },
    ],
  },
  {
    id: "counseling",
    title: "Free counseling",
    intro:
      "A HUD-approved counselor will review your actual file with you and hand you a written action plan — free. It is the single most underused tool on this page.",
    stage: "ready",
    ctaLabel: "Find a free counselor near you",
    ctaHref: "/tools/find-housing-counselor",
    tools: [
      {
        id: "hud-locator",
        name: "HUD counselor locator",
        source: "Government",
        what: "Search HUD-approved agencies by ZIP, with specialty and language filters plus referral hotlines.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "Available through the agency",
        personalization: "Highest — a person reviews your file",
        dataNeeded: "Full financial picture in session",
        nextStep: "Book a pre-purchase session",
        href: "/tools/find-housing-counselor",
        internal: true,
      },
      {
        id: "nonprofits",
        name: "National nonprofit programs",
        source: "Nonprofit",
        what: "NeighborWorks, NACA, Habitat, and NFCC compared on who they fit and what they require.",
        cost: "Free or low",
        noCreditPull: "Yes",
        certificate: "Varies by program",
        personalization: "High",
        dataNeeded: "Income and household details",
        nextStep: "Pick the org that matches your barrier",
        href: "/tools/nonprofit-mortgage-help",
        internal: true,
      },
      {
        id: "hope-hotline",
        name: "Homeowner's HOPE Hotline",
        source: "Nonprofit",
        what: "24/7 free counseling line, including urgent foreclosure and payment-trouble help.",
        cost: "Free",
        noCreditPull: "Yes",
        certificate: "No",
        personalization: "High",
        dataNeeded: "Loan and hardship details",
        nextStep: "Call (888) 995-4673",
        href: "tel:+18889954673",
      },
    ],
  },
];

export const COMPARE_ROWS: { key: keyof ToolEntry; label: string }[] = [
  { key: "cost", label: "Cost" },
  { key: "noCreditPull", label: "No credit pull" },
  { key: "certificate", label: "Certificate available" },
  { key: "personalization", label: "Personalization" },
  { key: "dataNeeded", label: "Data needed" },
  { key: "nextStep", label: "Best next step" },
];

export const SELECTION_RULES: { label: string; text: string }[] = [
  {
    label: "Free to use",
    text: "Every tool here has a genuinely free path. Where a fee can appear — some course certificates — we say so in the table instead of burying it.",
  },
  {
    label: "Reputable source",
    text: "The list deliberately mixes government (CFPB, HUD, AnnualCreditReport), GSE (Fannie Mae), nonprofit (NeighborWorks, NFCC, Framework), marketplace, and lender-adjacent tools, so no single business model shapes the advice.",
  },
  {
    label: "Actionable",
    text: "A tool earns a spot only if it ends with something you can do — a number, a plan, a certificate, or an appointment. Score dashboards with no next step did not make it.",
  },
  {
    label: "Beginner friendly",
    text: "No industry vocabulary required, no account linking to your bank required to get a first answer, and no hard credit pull anywhere on this page.",
  },
];

export const DISCLOSURES = {
  calculators:
    "Education only. Calculator outputs are estimates based on the numbers you enter and are not a loan offer, a pre-approval, or a commitment to lend. Your actual payment depends on your lender, rate, taxes, and insurance.",
  quizzes:
    "Education only. A readiness score is a self-assessment tool, not an eligibility determination. No lender is bound by it.",
  counseling:
    "Education only, not legal, tax, or lending advice. Home Approach is not a lender and does not administer assistance programs. Free HUD-approved counseling is available independently of anything on this site.",
};

export const FAQS: { id: string; q: string; a: string }[] = [
  {
    id: "free-calculators",
    q: "Are there genuinely free homebuying calculators, or do they all want my contact info?",
    a: "There are genuinely free ones. The CFPB's rate explorer requires nothing at all, and our affordability and cash-to-close calculators return a result without an account. Be cautious with calculators that demand a phone number before showing any output — that is a lead form wearing a calculator's clothes.",
  },
  {
    id: "readiness-quiz",
    q: "What does a homebuying readiness quiz actually measure?",
    a: "A good one scores five things: credit score, savings against down payment plus closing costs plus reserves, debt-to-income ratio, income stability, and how far out your timeline is. It should tell you which factor is weakest and what to do about it, not just hand you a number.",
  },
  {
    id: "no-credit-pull",
    q: "What does \"no credit pull\" actually mean when buying a home?",
    a: "It means no hard inquiry is placed on your credit report. Checking your own score or report is a soft inquiry and never affects your score. A hard pull happens when a lender formally underwrites an application. Even then, multiple mortgage inquiries within a 14 to 45 day shopping window are scored as one, so comparing lenders does not stack up damage.",
  },
  {
    id: "counselor-free",
    q: "Is housing counseling really free?",
    a: "Pre-purchase, budget, credit, rental, and foreclosure counseling from a HUD-approved agency is free or very low cost, and the fee must be disclosed to you upfront. The most common charge is a modest fee for a homebuyer education certificate course. Anyone asking for a large upfront payment, a wire, or gift cards is not a legitimate counselor.",
  },
  {
    id: "certificate",
    q: "Do I need a homebuyer education certificate?",
    a: "You need one if you plan to use most down payment assistance programs, and many first-time buyer loan products require it too. Take a free accepted course such as Fannie Mae HomeView early — certificates are typically valid for a year, and chasing one late is a common closing delay.",
  },
  {
    id: "order",
    q: "In what order should I use these tools?",
    a: "Quiz first to find your weak spot, credit report and score second because fixes take the longest, affordability calculator third once you know your rate range, course fourth so the certificate is banked, and a free counselor whenever your numbers are complicated or you want a written plan.",
  },
];

export const FREE_READINESS_TOOLS_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      inLanguage: "en-US",
      isPartOf: { "@type": "WebSite", name: "Home Approach", url: SITE },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: SITE,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Tools", item: `${SITE}/tools` },
        { "@type": "ListItem", position: 3, name: PAGE_TITLE, item: PAGE_URL },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
